import React, { useEffect, useMemo, useState } from "react";
import { formatDateTime, getTimezoneInfo } from '../../utils/dateTimeUtils';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  UncontrolledDropdown,
  Alert,
  Spinner,
  Form,
  FormGroup,
} from "reactstrap";
import DeleteModal from "../../Components/Common/DeleteModal";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import Widgets from "./Widgets";
import TableContainer from "../../Components/Common/TableContainer";
import { APIKeys, CreatedBy, ExpiryDate, Name, Status, CreatedDate } from "./APIKeyCol";
import { useDispatch, useSelector } from "react-redux";
import { getAPIKey } from "../../slices/thunks";
// TODO: Import missing Redux actions when available:
// clearError, clearSuccess, getAIServiceAPIKeys, updateAPIKey, createAPIKey, deleteAPIKey
import { createSelector } from "reselect";

const APIKey = () => {
  document.title = "API Key | ForecastNest - Crypto & Stock Prediction Platform";
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [apiKeyName, setApiKeyName] = useState('');
  const [licenseType, setLicenseType] = useState('free');
  const [isEdit, setIsEdit] = useState(false);

  const handleClose = () => {
    setShow(false);
    setApiKeyName('');
    setLicenseType('free');
    setIsEdit(false);
    setSelectedKey(null);
    // TODO: Implement Redux actions for clearing errors
    // dispatch(clearError());
    // dispatch(clearSuccess());
  };
  
  const handleShow = () => setShow(true);

  const onClickDelete = (key) => {
    setSelectedKey(key);
    setDeleteModal(true);
  };

  const onClickEdit = (key) => {
    setSelectedKey(key);
    setApiKeyName(key.name);
    setLicenseType(key.license_type || 'free');
    setIsEdit(true);
    setShow(true);
  };

  const onClickRegenerate = (key) => {
    setSelectedKey(key);
    // For now, we'll implement this later as it requires private key
    alert('Regenerate functionality requires private key implementation');
  };

  const apikeyData = createSelector(
    (state) => state.APIKey,
    (apiKey) => ({
      apiKey: apiKey.apiKey,
      aiServiceKeys: apiKey.aiServiceKeys,
      loading: apiKey.loading,
      error: apiKey.error,
      success: apiKey.success
    })
  );
  const { apiKey: APIKeyData, aiServiceKeys, loading, error, success } = useSelector(apikeyData);

  useEffect(() => {
    dispatch(getAPIKey());
    // TODO: Implement getAIServiceAPIKeys action
    // dispatch(getAIServiceAPIKeys());
  }, [dispatch]);

  // Refresh data when operations succeed
  useEffect(() => {
    if (success) {
      dispatch(getAPIKey());
      handleClose();
    }
  }, [success, dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!apiKeyName.trim()) {
      return;
    }

    if (isEdit && selectedKey) {
      // TODO: Implement updateAPIKey action
      // dispatch(updateAPIKey({
      //   publicKey: selectedKey.apikey,
      //   updates: {
      //     app_name: apiKeyName,
      //     license_type: licenseType
      //   }
      // }));
      console.log('Update API key:', {
        publicKey: selectedKey.apikey,
        updates: {
          app_name: apiKeyName,
          license_type: licenseType
        }
      });
    } else {
      // TODO: Implement createAPIKey action
      // dispatch(createAPIKey({
      //   appName: apiKeyName,
      //   licenseType: licenseType
      // }));
      console.log('Create API key:', {
        appName: apiKeyName,
        licenseType: licenseType
      });
    }
  };

  const handleDelete = () => {
    if (selectedKey) {
      // TODO: Implement deleteAPIKey action
      // dispatch(deleteAPIKey(selectedKey.apikey));
      console.log('Delete API key:', selectedKey.apikey);
      setDeleteModal(false);
      setSelectedKey(null);
    }
  };

  const toggleStatus = (key) => {
    // TODO: Implement updateAPIKey action
    // dispatch(updateAPIKey({
    //   publicKey: key.apikey,
    //   updates: {
    //     is_active: key.status !== 'Active'
    //   }
    // }));
    console.log('Toggle status for API key:', {
      publicKey: key.apikey,
      updates: {
        is_active: key.status !== 'Active'
      }
    });
  };

  // Checked All
  const checkedAll = () => {
    const checkall = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".orderCheckBox");

    if (checkall.checked) {
      ele.forEach((ele) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele) => {
        ele.checked = false;
      });
    }
  };
  
  const columns = useMemo(
    () => [
      {
        header: (
          <Input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell) => {
          return (
            <Input
              type="checkbox"
              className="orderCheckBox form-check-input"
              value={cell.getValue()}
            />
          );
        },
        id: "#",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: (cell) => {
          return <Name {...cell} />;
        },
      },
      {
        header: "Created By",
        accessorKey: "createBy",
        enableColumnFilter: false,
        cell: (cell) => {
          return <CreatedBy {...cell} />;
        },
      },
      {
        header: "API Key",
        accessorKey: "apikey",
        enableColumnFilter: false,
        cell: (cell) => {
          return <APIKeys {...cell} />;
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (cell) => {
          return <Status {...cell} />;
        },
      },
      {
        header: "License Type",
        accessorKey: "license_type",
        enableColumnFilter: false,
        cell: (cell) => {
          return <span className="badge bg-info text-white">{cell.getValue()}</span>;
        },
      },
      {
        header: "Created Date",
        accessorKey: "create_date",
        enableColumnFilter: false,
        cell: (cell) => {
          return <CreatedDate {...cell} />;
        },
      },
      {
        header: "Expiry Date",
        accessorKey: "expiry_date",
        enableColumnFilter: false,
        cell: (cell) => {
          return <ExpiryDate {...cell} />;
        },
      },
      {
        header: "Action",
        disableFilters: true,
        enableSorting: false,
        cell: (cell) => {
          return (
            <UncontrolledDropdown className="dropdown">
              <DropdownToggle
                role="button"
                tag="button"
                className="btn btn-soft-secondary btn-sm dropdown"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu dropdown-menu-end">
                <li>
                  <DropdownItem
                    className="edit-item-btn"
                    onClick={() => onClickEdit(cell.row.original)}
                  >
                    Edit
                  </DropdownItem>
                </li>
                <li>
                  <DropdownItem
                    className="regenerate-api-btn"
                    onClick={() => onClickRegenerate(cell.row.original)}
                  >
                    Regenerate Key
                  </DropdownItem>
                </li>
                <li>
                  <DropdownItem 
                    className="toggle-status-btn"
                    onClick={() => toggleStatus(cell.row.original)}
                  >
                    {cell.row.original.status === "Active" ? "Disable" : "Enable"}
                  </DropdownItem>
                </li>
                <li>
                  <DropdownItem
                    className="remove-item-btn"
                    onClick={() => onClickDelete(cell.row.original)}
                  >
                    Delete
                  </DropdownItem>
                </li>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <DeleteModal
          show={deleteModalMulti}
          onDeleteClick={() => {
            setDeleteModalMulti(false);
          }}
          onCloseClick={() => setDeleteModalMulti(false)}
        />
        <Container fluid>
          <BreadCrumb title="API Key" pageTitle="Apps" />

          {error && Object.keys(error).length > 0 && (
            <Alert color="danger" className="alert-dismissible">
              <strong>Error: </strong> {typeof error === 'string' ? error : JSON.stringify(error)}
            </Alert>
          )}

          <Row>
            <Widgets />
          </Row>

          <Row>
            <Col lg={12}>
              <Card id="apiKeyList">
                <CardHeader className="d-flex align-items-center">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <h5 className="card-title mb-0">API Keys</h5>
                    <small className="text-muted">
                      <i className="ri-time-zone-line me-1"></i>
                      Times shown in your local timezone ({getTimezoneInfo().offset})
                    </small>
                  </div>
                  <div className="d-flex gap-1 flex-wrap">
                    <Button
                      type="button"
                      color="primary"
                      className="btn create-btn"
                      onClick={handleShow}
                      disabled={loading}
                    >
                      <i className="ri-add-line align-bottom me-1"></i> Add API Key
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  <div>
                    {loading ? (
                      <div className="text-center">
                        <Spinner color="primary" />
                        <p>Loading API keys...</p>
                      </div>
                    ) : (
                      <TableContainer
                        columns={columns}
                        data={APIKeyData || []}
                        customPageSize={8}
                        divClass="table-responsive table-card mb-3"
                        tableClass="table align-middle table-nowrap mb-0"
                        theadClass="table-light"
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* AI Service Keys Section */}
          {aiServiceKeys && aiServiceKeys.length > 0 && (
            <Row>
              <Col lg={12}>
                <Card>
                  <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">AI Service API Keys</h5>
                      <small className="text-muted">
                        <i className="ri-time-zone-line me-1"></i>
                        Times shown in your local timezone ({getTimezoneInfo().offset})
                      </small>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <div className="table-responsive">
                      <table className="table table-borderless table-centered align-middle table-nowrap mb-0">
                        <thead className="text-muted table-light">
                          <tr>
                            <th>Service Name</th>
                            <th>API Key</th>
                            <th>License Type</th>
                            <th>Status</th>
                            <th>Last Used</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aiServiceKeys.map((key) => (
                            <tr key={key.id}>
                              <td>{key.app_name}</td>
                              <td>
                                <input 
                                  type="text" 
                                  className="form-control" 
                                  value={key.public_key} 
                                  readOnly 
                                />
                              </td>
                              <td>
                                <span className="badge bg-success">{key.license_type}</span>
                              </td>
                              <td>
                                <span className={`badge ${key.is_active ? 'bg-success' : 'bg-danger'}`}>
                                  {key.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>{key.last_used_at ? formatDateTime(key.last_used_at) : 'Never'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>

      <Modal isOpen={show} toggle={handleClose}>
        <ModalHeader toggle={handleClose}>
          {isEdit ? 'Edit API Key' : 'Create API Key'}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="api-key-name">
                API Key Name <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                className="form-control"
                id="api-key-name"
                placeholder="Enter API key name"
                value={apiKeyName}
                onChange={(e) => setApiKeyName(e.target.value)}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="license-type">License Type</Label>
              <Input
                type="select"
                className="form-control"
                id="license-type"
                value={licenseType}
                onChange={(e) => setLicenseType(e.target.value)}
              >
                <option value="free">Free (100 calls/day)</option>
                <option value="basic">Basic (1,000 calls/day)</option>
                <option value="pro">Pro (10,000 calls/day)</option>
                <option value="enterprise">Enterprise (100,000 calls/day)</option>
              </Input>
            </FormGroup>
            <div className="hstack gap-2 justify-content-end">
              <Button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Close
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    {isEdit ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  isEdit ? 'Update API Key' : 'Create API Key'
                )}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default APIKey;
